import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { isValidString, shouldDisplayAddPointElement } from '../helpers/utils';

import { PointCardBase } from './point-card-base';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import topicData from '../helpers/state/topic-data';

@customElement('list-point-card')
export class ListPointCard extends PointCardBase {
    static amplitude_callsite: string = 'List Point Card';

    @state()
    multiSelectionMode: boolean = false;

    private async onCardClick() {
        this.dispatchEvent(new CustomEvent('on-card-click'));
    }

    private async onDeselectPoint() {
        if (this.point) {
            await topicData.mapComponent?.onDeselectPoint(this.point);
        }
    }

    render() {
        return html`
            <div class="list-point-card" @mouseenter=${this.onHoverMarker} @mouseleave=${this.onExitHoverMarker} title=${this.point?.name}>
                ${this.multiSelectionMode ? html`
                        <div class="deselect-control" @click="${this.onDeselectPoint}">
                            <mwc-icon>close</mwc-icon>
                        </div>`
                    : ''
                    }
                <div class="list-point-card__image-wrapper" @click="${this.onCardClick}">
                    <img @error="${(e: Event) => this.handleImageError(e)}" src=${this.hasImages ? this.firstImage : this.associatedMarker?.iconUrl} class="list-point-card__image" style="${this.hasImages ? '' : 'width: unset'} object-fit: ${topicData.topic?.image_display ?? 'cover'}"/>
                </div>
                <div class="list-point-card__content_image" @click="${this.onCardClick}">
                    <span class="list-point-card__category-wrapper">
                        ${topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined && this.hasImages // prevent duplicate number icon
                            ? html`<img @error="${(e: Event) => this.handleImageError(e)}" src="${makeColorsIntoNumberSvg(getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid),this.point?.number, getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid))}"
                                        class="list-point-card__point-number"
                                        width="28"
                                        height="28"/>`
                            : ''
                        }
                    </span>
                    ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.title?.hide_on_point_card) ? html`
                        <span class="list-point-card__title">
                            ${this.point?.featured === true
                                ? html`
                                    <mwc-icon class="list-point-card__title--featured">star</mwc-icon>`
                                : ''
                            }
                            ${isValidString(this.point?.name) ? html`<h2 class="list-point-card__title-text">${this.point?.featured === true ? `${topicData.topic?.featured_name ?? 'Featured'}:` : ''} ${this.point?.name}</h2>`: ''}
                    </span>
                    ` : ''}
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 36px;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .list-point-card {
                word-wrap: break-word;
                border-radius: 4px;
                display: flex;
                height: 40px;
                cursor: pointer;
                cursor: pointer;
                font-weight: 400;
                background-color: white;
                border: 1px solid var(--theme-border-color);
            }

            .list-point-card__content_image {
                padding: 4px 6px 4px 2px;
                width: 60%;
                overflow: hidden;
                opacity: 0.98;
                flex: 1;
                display: flex;
                align-items: center;
            }

            .list-point-card__content_no_image {
                padding: 12px 16px;
                width: 100%;
                background: white;
                overflow: hidden;
                opacity: 0.98;
            }

            .list-point-card__image {
                width: 100%;
                height: 100%;
                border-radius: 2px;
                object-fit: cover;
                opacity: 1;
                background-color: var(--theme-lighter-text-color);
            }

            .list-point-card__point-number {
                margin-right: 2px;
                z-index: 1;
            }

            .list-point-card__description {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .list-point-card__cover {
                height: 40px;
                background: linear-gradient(to top, white, transparent 100%);
                margin-top: -50px;
                padding: 5px;
                width: 100%;
                margin-left: -5px;
                position: relative;
            }

            .list-point-card__category-wrapper {
                display: flex;
            }

            .list-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 5px 8px;
                gap: 5px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .list-point-card__image-wrapper {
                display: flex;
                width: 10%;
                overflow: hidden;
                padding: 4px;
                align-items: center;
                opacity: 0.98;
                gap: 0.5rem;
                justify-content: center;
                box-shadow: 0px 0px 4px 0px var(--theme-border-color);
            }

            .space-wrapper {
                flex: 1;
            }

            .list-point-card__title-text {
                font-size: 14px;
                color: var(--main-primary-color);
                margin: 4px 0 0;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
                font-weight: 400;
            }

            .list-point-card__address {
                color: var(--theme-lighter-text-color);
                display: block;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .list-point-card__event {
                color: var(--theme-lighter-text-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .list-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 20px;
                width: 16px;
                padding-right: 4px;
            }

            .list-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .list-point-card__title {
                display: flex;
            }

            .list-point-card:hover {
                background-color: var(--theme-border-color);
                font-weight: 700;
            }

            .list-point-card:hover .list-point-card__image-wrapper {
                transform: rotate(-5deg);
            }

            .deselect-control {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 0.25rem;
                cursor: pointer;
                background-color: var(--theme-opacity-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 2rem;
                font-size: 14px;
            }
        `;
    }
}